import axios from 'axios';
import { Note, CreateNoteData, UpdateNoteData, NoteQuery, NoteResponse, NoteCounts } from '../types/note';

const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || '/api';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: `${API_BASE_URL}/notes`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refreshToken,
          });
          
          const { accessToken, refreshToken: newRefreshToken } = response.data;
          localStorage.setItem('accessToken', accessToken);
          localStorage.setItem('refreshToken', newRefreshToken);
          
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

class NoteService {

  async getNotes(query: NoteQuery = {}): Promise<NoteResponse> {
    const response = await apiClient.get<NoteResponse>('', { params: query });
    return response.data;
  }

  async getNote(id: number): Promise<Note> {
    const response = await apiClient.get<Note>(`/${id}`);
    return response.data;
  }

  async getNotesByDate(date: string): Promise<Note[]> {
    const response = await apiClient.get<Note[]>(`/date/${date}`);
    return response.data;
  }

  async getNotesByDateRange(startDate: string, endDate: string): Promise<Note[]> {
    const response = await apiClient.get<Note[]>('/range/date', {
      params: { startDate, endDate }
    });
    return response.data;
  }

  async searchNotes(query: string, limit: number = 10): Promise<Note[]> {
    const response = await apiClient.get<Note[]>('/search', {
      params: { q: query, limit }
    });
    return response.data;
  }

  async getNoteCount(date: string): Promise<{ date: string; count: number }> {
    const response = await apiClient.get<{ date: string; count: number }>(`/count/${date}`);
    return response.data;
  }

  async getNoteCounts(startDate: string, endDate: string): Promise<NoteCounts> {
    const response = await apiClient.get<NoteCounts>('/counts/range', {
      params: { startDate, endDate }
    });
    return response.data;
  }

  async createNote(noteData: CreateNoteData): Promise<Note> {
    const response = await apiClient.post<Note>('', noteData);
    return response.data;
  }

  async updateNote(id: number, noteData: UpdateNoteData): Promise<Note> {
    const response = await apiClient.put<Note>(`/${id}`, noteData);
    return response.data;
  }

  async deleteNote(id: number): Promise<{ success: boolean }> {
    const response = await apiClient.delete<{ success: boolean }>(`/${id}`);
    return response.data;
  }

  async restoreNote(id: number): Promise<Note> {
    const response = await apiClient.patch<Note>(`/${id}/restore`);
    return response.data;
  }
}

export const noteService = new NoteService(); 